console.log('NEW FILE LOADED');
import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { isClickedOutside, isMobileBreakpoint, isTouchDevice, mediaQueryLarge } from '@theme/utilities';
 
export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  #pointerenterHandler = null;
  #touchDismissHandler = null;
  timer = null;
 
  connectedCallback() {
    super.connectedCallback();
    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }
 
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }
 
  #openQuickAddModal() {
    const quickAddComponent = this.querySelector('quick-add-component');
    if (!quickAddComponent) return;
    quickAddComponent.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
  }
 
  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;
 
    const pointerenterHandler = () => {
      if (dialog.open) return;
      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };
 
    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }
 
  #removeDesktopListeners() {
    const { trigger } = this.refs;
 
    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }
 
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
 
  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };
 
  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;
    const hotspotsContainer = this.parentElement;
 
    if (!hotspotsContainer) return;
 
    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;
 
    const containerRect = hotspotsContainer.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
 
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');
 
    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();
 
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');
 
    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;
 
    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;
 
    let x = 'right';
    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }
 
    let y = 'bottom';
    let verticalOffset = 0;
 
    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;
 
      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';
 
        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
          y = 'bottom';
        }
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }
 
    dialog.dataset.placement = `${x},${y}`;
 
    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }
 
    return new Promise((resolve) => setTimeout(resolve, 100));
  }
 
  #handlePointerLeave = (e) => {
    const { dialog, trigger } = this.refs;
 
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
 
    if (!dialog.open) return;
 
    const isLeavingTrigger = e.target === trigger;
    const isLeavingDialog = e.target === dialog;
    const isGoingToDialog =
      e.relatedTarget === dialog ||
      (e.relatedTarget instanceof Element && e.relatedTarget.closest('dialog') === dialog);
    const isGoingToTrigger = e.relatedTarget === trigger;
 
    if ((isLeavingTrigger && !isGoingToDialog) || (isLeavingDialog && !isGoingToTrigger)) {
      this.closeDialog();
    }
  };
 
  getHotspotProductLink() {
    return this.refs.productLink || null;
  }
 
  handleHotspotClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { dialog } = this.refs;
    if (dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };
 
  showDialog = async () => {
    const { dialog } = this.refs;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
 
    // On mobile/touch: use touchstart to dismiss, not click
    if (isMobileBreakpoint() || isTouchDevice()) {
      this.#touchDismissHandler = (e) => {
        if (!dialog.contains(e.target) && e.target !== this.refs.trigger) {
          this.closeDialog();
        }
      };
      setTimeout(() => {
        document.addEventListener('touchstart', this.#touchDismissHandler, { passive: true });
      }, 400);
    } else {
      setTimeout(() => {
        document.body.addEventListener('click', this.lightDismissMouse);
      }, 300);
      dialog.addEventListener('pointerleave', this.#handlePointerLeave);
    }
 
    document.body.addEventListener('keydown', this.lightDismissKeyboard);
    document.body.addEventListener('keyup', this.lightDismissKeyboard);
  };
 
  closeDialog = async () => {
    const { dialog, trigger } = this.refs;
    dialog.dataset.closing = 'true';
    dialog.close();
 
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
 
    if (this.#touchDismissHandler) {
      document.removeEventListener('touchstart', this.#touchDismissHandler);
      this.#touchDismissHandler = null;
    }
 
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };
 
  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };
 
  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      this.closeDialog();
    }
  };
}
 
customElements.define('product-hotspot-component', ProductHotspotComponent);